import { Component, OnInit, OnDestroy, AfterViewInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { NgxSpinnerService } from "ngx-spinner";

import { first } from "rxjs/operators";
import { User } from "src/app/models/user-model";

import { UserService } from "src/app/services/user/user.service";
import { GenerateErrors } from "src/app/utils/generate-errors";
import { Webflow } from "src/app/utils/webflow";
import {
  FacebookLoginProvider,
  GoogleLoginProvider,
  SocialAuthService,
} from "angularx-social-login";

import Swal from "sweetalert2";
import { AuthService } from "src/app/services/auth/auth.service";

import intlTelInput from "intl-tel-input";
import { Location } from "@angular/common";
import { CustomService } from "src/app/services/custom/custom.service";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
})
export class LoginComponent implements OnInit, AfterViewInit {
  loginForm: FormGroup;
  forgetPasswordForm: FormGroup;
  loading = false;
  submitted = false;
  step = "Se connecter";

  return_url: string = "";
  all_params: any;

  stepLoginPhone: string = "";
  phoneNumber: string = "";
  country_code_phone: string = "";
  country_phone: string = "FR";

  phoneInputElement!: any;
  verificationCode: string = "";

  constructor(
    private spinner: NgxSpinnerService,
    private formBuilder: FormBuilder,
    private router: Router,
    private authenticationService: AuthService,
    private userService: UserService,
    private activatedRoute: ActivatedRoute,
    private socialauthenticationService: SocialAuthService,
    private location: Location,
    private customService: CustomService
  ) {
    this.loginForm = this.formBuilder.group({
      identifier: ["", [Validators.required, Validators.email]],
      password: ["", [Validators.required, Validators.minLength(6)]],
    });
    this.forgetPasswordForm = this.formBuilder.group({
      email: ["", [Validators.required, Validators.email]],
    });
  }

  ngOnInit() {
    this.activatedRoute.queryParams.subscribe((params) => {
      this.return_url = params.return ? params.return : "";
      this.all_params = params ? params : null;

      this.stepLoginPhone = params.step_phone ? params.step_phone : "";
      this.phoneNumber = params.phoneNumber ? params.phoneNumber : "";
      this.country_code_phone = params.country_code_phone
        ? params.country_code_phone
        : "";
      this.country_phone = params.country_phone ? params.country_phone : "FR";
    });
  }

  ngAfterViewInit(): void {
    this.initPhone(this.country_phone);
    Webflow.setIFrameHeight();
  }

  get formControl() {
    return this.loginForm.controls;
  }
  get forgetFormControl() {
    return this.forgetPasswordForm.controls;
  }

  redirectAfterLogin() {
    if (this.return_url != "") {
      let my_params = {};
      this.activatedRoute.queryParams.subscribe((params) => {
        my_params = { ...params }; // Create a shallow copy of params
      });

      delete my_params["return"];

      Webflow.myAction(this.router, this.return_url, my_params);
    } else {
      Webflow.myAction(this.router, "dashboard", {});
    }
  }

  navigateTo(actionName: string) {
    Webflow.myAction(this.router, actionName, {});
  }

  onSubmit() {
    this.submitted = true;

    // stop here if form is invalid
    if (this.loginForm.invalid) {
      Webflow.setIFrameHeight();
      Swal.fire({
        title: "Erreur",
        text: "Veuillez vérifier vos informations",
        icon: "error",
        position: "top",
      });

      return;
    }

    this.spinner.show();
    this.loading = true;
    this.authenticationService
      .login(this.formControl.identifier.value, this.formControl.password.value)
      .pipe(first())
      .subscribe(
        (data) => {
          this.userService.get(data.jwt).subscribe((user) => {
            let userJson = User.toJson(user, data.jwt, false, user.role.type);
            if (userJson != null) {
              if (
                userJson.role == "artist" ||
                userJson.role == "manager_studio"
              ) {
                localStorage.setItem("studiomapUser", JSON.stringify(userJson));
                this.authenticationService.callCurrentUserSubject(userJson);
                this.redirectAfterLogin();
              } else {
                Webflow.setIFrameHeight();
                Swal.fire({
                  title: "Attention",
                  text: "Vous n'êtes pas autorisé à accéder à cette platform",
                  icon: "error",
                  position: "top",
                });
              }
            } else {
              Webflow.setIFrameHeight();
              Swal.fire({
                title: "Erreur",
                text: "Profile incomplet!",
                icon: "error",
                position: "top",
              });
            }
          });
        },
        (error) => {
          Webflow.setIFrameHeight();
          if (GenerateErrors.check_if_is_custom_error(error)) {
            if (GenerateErrors.get_error_id(error) == "confirm.email") {
              Swal.fire({
                title: "Attention",
                text: GenerateErrors.get_message_error(error),
                icon: "error",
                showCancelButton: true,
                confirmButtonText: "je n'ai pas reçu l'émail d'activation",
                cancelButtonText: "fermer",
                position: "top",
                cancelButtonColor: "black",
                confirmButtonColor: "#D63333",
              }).then((result) => {
                if (result.isConfirmed) {
                  this.spinner.show();
                  this.authenticationService
                    .resendConfirmation(this.formControl.identifier.value)
                    .subscribe(
                      (res) => {
                        Swal.fire({
                          title: "Envoyé",
                          text: "Un nouveau émail d'activation a été envoyé à votre boite.",
                          icon: "success",
                          position: "top",
                        });
                      },
                      (err) => {
                        Swal.fire({
                          title: "Érreur",
                          text: "Une érreur est survenue",
                          icon: "error",
                          position: "top",
                        });
                      }
                    )
                    .add(() => {
                      this.spinner.hide();
                    });
                }
              });
            } else {
              let title =
                GenerateErrors.get_error_id(error) == "coming.soon"
                  ? "Bientôt disponible"
                  : "Érreur";

              Swal.fire({
                title: title,
                text: GenerateErrors.get_message_error(error),
                icon:
                  GenerateErrors.get_error_id(error) == "coming.soon"
                    ? "info"
                    : "error",
                position: "top",
              });
            }
          } else {
            Swal.fire({
              title: "Érreur",
              text: "Erreur de connexion",
              icon: "error",
              position: "top",
            });
          }
        }
      )
      .add(() => {
        this.spinner.hide();
        this.loading = false;
      });
  }

  onSubmitForgetPassword() {
    this.submitted = true;

    // stop here if form is invalid
    if (this.forgetPasswordForm.invalid) {
      Webflow.setIFrameHeight();
      Swal.fire({
        title: "Érreur",
        text: "l'adresse e-mail est requise",
        icon: "error",
        position: "top",
      });
      return;
    }

    this.spinner.show();
    this.authenticationService
      .forgotPassword(this.forgetPasswordForm.value.email)
      .subscribe(
        (res) => {
          Swal.fire({
            title: "Envoyé",
            text: "Le lien de réinitialisation a été envoyé.\nVeuillez vérifier votre couriel.",
            icon: "success",
            position: "top",
          });
          this.step = "Se connecter";
          this.submitted = false;
          this.forgetPasswordForm.reset();
        },
        (err) => {
          Swal.fire({
            title: "Érreur",
            text: "Une érreur est survenue",
            icon: "error",
            position: "top",
          });
        }
      )
      .add(() => {
        this.spinner.hide();
        Webflow.setIFrameHeight();
      });
  }

  checkIfAccountExist(provider: string, user: any) {
    this.spinner.show();
    this.authenticationService
      .loginWith(provider, "&access_token=" + user.authToken)
      .subscribe(
        (data: any) => {
          this.spinner.show();
          this.userService
            .get(data.jwt)
            .subscribe(
              (user) => {
                let userJson = User.toJson(
                  user,
                  data.jwt,
                  false,
                  user.role.type
                );

                if (userJson != null) {
                  localStorage.setItem(
                    "studiomapUser",
                    JSON.stringify(userJson)
                  );
                  this.authenticationService.callCurrentUserSubject(userJson);
                  if (userJson.password_edit_by == "admin") {
                    Webflow.setIFrameHeight();
                    Webflow.myAction(this.router, "mon-profil", {
                      form: "reset-password",
                    });
                    Swal.fire({
                      title: "Attention",
                      text: "Veuillez réinitialiser votre mot de passe",
                      icon: "info",
                      position: "top",
                    });
                  } else {
                    this.redirectAfterLogin();
                  }
                } else {
                  let params: any = {};
                  params.step = "form";
                  params.provider = provider;
                  params.user_id = user.id;
                  params.email = user.email;
                  params.username = user.username;
                  params.token = data.jwt;
                  if (this.return_url != "") {
                    params.return = this.return_url;
                  }

                  Webflow.myAction(this.router, "register", params);
                }
              },
              (err) => {
                Webflow.setIFrameHeight();
                Swal.fire({
                  title: "Error",
                  text: "Impossible de se connecter avec se compte. \n veuillez contacter le support à support@studiomap.fr .",
                  icon: "error",
                  position: "top",
                });
              }
            )
            .add(() => {
              this.spinner.hide();
            });
        },
        (err) => {
          Webflow.setIFrameHeight();
          Swal.fire({
            title: "Error",
            text: "Une érreur s'est produite veuillez réessayer",
            icon: "error",
            position: "top",
          });
        }
      )
      .add(() => {
        this.spinner.hide();
      });
  }

  loginWithFB() {
    this.socialauthenticationService
      .signIn(FacebookLoginProvider.PROVIDER_ID)
      .then((user) => {
        this.checkIfAccountExist("facebook", user);
      })
      .catch((err) => {
        Webflow.setIFrameHeight();
        Swal.fire({
          title: "Error",
          text: "Une érreur s'est produite veuillez réessayer",
          icon: "error",
          position: "top",
        });
      });
  }

  loginWithGoogle() {
    this.socialauthenticationService
      .signIn(GoogleLoginProvider.PROVIDER_ID, {
        scope: "profile email",
      })
      .then((user) => {
        this.checkIfAccountExist("google", user);
      })
      .catch((err) => {
        Webflow.setIFrameHeight();
        Swal.fire({
          title: "Error",
          text: "Une érreur s'est produite veuillez réessayer",
          icon: "error",
          position: "top",
        });
      });
  }

  loginWithPhone() {
    Webflow.setIFrameHeight();

    if (this.stepLoginPhone == "send-code") {
      if (this.phoneNumber != "") {
        if (this.phoneNumber.length != 9) {
          Swal.fire({
            title: "Attention",
            text: "Veuillez vérifier votre numéro de téléphone",
            icon: "info",
            position: "top",
          });
        } else {
          // send code and redirect to validation page
          const countryData = this.phoneInputElement.getSelectedCountryData();
          this.country_code_phone = "+" + countryData.dialCode;
          this.country_phone = countryData.iso2;
          this.customService
            .checkIssetAccount({
              check: "login_phone",
              phone: this.phoneNumber,
              country_code_phone: this.country_code_phone,
              country_phone: this.country_phone,
            })
            .subscribe(
              (res: any) => {
                if (res.result == true) {
                  this.customService
                    .sendPhoneConfirmation({
                      phone: this.phoneNumber,
                      country_code_phone: this.country_code_phone,
                    })
                    .subscribe(
                      (resultConfirmation: any) => {
                        console.log("===> done send confirmation code !!");

                        Webflow.myAction(this.router, "login", {
                          step_phone: "validation-code",
                          phoneNumber: this.phoneNumber,
                          country_code_phone: this.country_code_phone,
                          country_phone: this.country_phone,
                        });
                      },
                      (error) => {
                        if (GenerateErrors.check_if_is_custom_error(error)) {
                          Swal.fire({
                            title: "Érreur",
                            text: GenerateErrors.get_message_error(error),
                            icon: "error",
                            position: "top",
                          });
                        } else {
                          Swal.fire({
                            title: "Erreur",
                            text: "Une érreur est survenue",
                            icon: "error",
                            position: "top",
                          });
                        }
                      }
                    );
                } else {
                  Swal.fire({
                    title: "Erreur",
                    text: "Échec de la connexion, aucun compte n'a été créé avec ce numéro de téléphone.",
                    icon: "error",
                    position: "top",
                  });
                }
              },
              (err) => {
                Swal.fire({
                  title: "Erreur",
                  text: "Une érreur est survenu",
                  icon: "error",
                  position: "top",
                });
              }
            );
        }
      } else {
        Swal.fire({
          title: "Erreur",
          text: "Veuillez saisir votre numéro de téléphone",
          icon: "error",
          position: "top",
        });
      }
    } else if (this.stepLoginPhone == "validation-code") {
      if (this.verificationCode.length != 4) {
        Swal.fire({
          title: "Attention",
          text: "Veuillez vérifier votre code de vérification",
          icon: "info",
          position: "top",
        });
      } else {
        var fullPhoneNumber = this.country_code_phone + this.phoneNumber;
        var verificationCode = this.verificationCode;
        this.spinner.show();
        this.authenticationService
          .loginWith(
            "phone",
            "&fullPhoneNumber=" +
              fullPhoneNumber +
              "&verificationCode=" +
              verificationCode
          )
          .subscribe(
            (data: any) => {
              this.spinner.show();
              this.userService
                .get(data.jwt)
                .subscribe(
                  (user) => {
                    let userJson = User.toJson(
                      user,
                      data.jwt,
                      false,
                      user.role.type
                    );

                    if (userJson != null) {
                      localStorage.setItem(
                        "studiomapUser",
                        JSON.stringify(userJson)
                      );
                      this.authenticationService.callCurrentUserSubject(
                        userJson
                      );
                      if (userJson.password_edit_by == "admin") {
                        Webflow.setIFrameHeight();
                        Webflow.myAction(this.router, "mon-profil", {
                          form: "reset-password",
                        });
                        Swal.fire({
                          title: "Attention",
                          text: "Veuillez réinitialiser votre mot de passe",
                          icon: "info",
                          position: "top",
                        });
                      } else {
                        this.redirectAfterLogin();
                      }
                    } else {
                    }
                  },
                  (err) => {
                    Webflow.setIFrameHeight();
                    Swal.fire({
                      title: "Error",
                      text: "Impossible de se connecter avec se compte. \n veuillez contacter le support à support@studiomap.fr .",
                      icon: "error",
                      position: "top",
                    });
                  }
                )
                .add(() => {
                  this.spinner.hide();
                });
            },
            (error) => {
              if (GenerateErrors.check_if_is_custom_error(error)) {
                Swal.fire({
                  title: "Érreur",
                  text: GenerateErrors.get_message_error(error),
                  icon: "error",
                  position: "top",
                });
              } else {
                Swal.fire({
                  title: "Erreur",
                  text: "Une érreur est survenue",
                  icon: "error",
                  position: "top",
                });
              }
            }
          )
          .add(() => {
            this.spinner.hide();
          });
      }
    } else {
      Webflow.myAction(this.router, "login", { step_phone: "send-code" });
    }
  }

  back() {
    this.location.back();
  }

  initPhone(countryCode: string) {
    const inputElement = document.getElementById("phone");
    if (inputElement) {
      this.phoneInputElement = intlTelInput(inputElement as HTMLInputElement, {
        initialCountry: countryCode,
        separateDialCode: true,
      });
    }
  }
}
